import {DefaultContainer} from "@shared/DefaultContainer.tsx";
import React, {useEffect, useMemo, useState} from "react";
import usePagination from "@components/shared/hooks/usePagination.tsx";
import {DefaultPageProps} from "@models/DefaultProps.ts";
import {ISolicitacaoOrcamento} from "@models/solicitacoes/orcamento.ts";
import {useOrcamento} from "@components/orcamento/hooks/useOrcamento.ts";
import {
    CreateAnexoOrcamentoComponent,
    OrcamentoAnexoType
} from "@components/orcamento/CreateAnexoOrcamentoComponent.tsx";
import {IArquivo} from "@models/sistema/IArquivo.ts";
import {
    Button,
    Chip,
    FormControl,
    Grid,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField
} from "@mui/material";
import {useAlert} from "@contexts/hooks/useAlert.ts";
import {ArquivoRow} from "@components/shared/arquivos/ArquivoRow.tsx";
import {fullHeight} from "@models/helpers.ts";

interface OrcamentoArquivosPageProps extends DefaultPageProps {
    preset: {
        orcamento: ISolicitacaoOrcamento | number,
        tipos?: OrcamentoAnexoType[]
    }
}

export function OrcamentoArquivosPage(props: OrcamentoArquivosPageProps) {
    const {useOrcamentoValue, getArquivosOrcamento} = useOrcamento()
    const {addPopUp, closePopUp} = useAlert()
    const [orcamento] = useOrcamentoValue(props.preset.orcamento)
    const [tipoSelected, setTiposSelected] = useState<OrcamentoAnexoType[]>(props.preset?.tipos ?? [])
    const [arquivos, setArquivos] = useState<IArquivo[]>([])

    const [pagination, setPagination] = usePagination({
        pagination: props.pageProps?.pagination,
        setPagination: props.pageProps?.setPagination,
        triggers: [{
            promise: (p) => {
                if (orcamento == null) return null as any;
                return getArquivosOrcamento({
                    data: {idOrcamento: orcamento.id, tipos: tipoSelected},
                    pagination: p.pagination
                })
                    .then(x => {
                        if (x.items != null) {
                            setArquivos(x.items)
                        }
                        setPagination(prev => ({...prev, ...x.pagination}))
                    })
            },
            eventName: 'process'
        }]
    })

    useEffect(() => {
        if (orcamento == null) return;
        setPagination(prev => ({...prev, page: 1, ignoreCache: true}))
    }, [orcamento]);

    const canChange = useMemo(() => {
        return props.preset.tipos == null
    }, [props.preset.tipos]);

    return (<DefaultContainer titulo={'Arquivos do orçamento'}
                              hideHeader={props.hideHeader}
                              UpperComponent={<Grid container size={12} spacing={1}>
                                  <Grid size={{xs: 12, md: 6}} component={FormControl}>
                                      <TextField label="Tipos" onChange={(v) => {
                                          if (!tipoSelected.includes(v.target.value as OrcamentoAnexoType)) {
                                              setTiposSelected(prev => [...prev, v.target.value as OrcamentoAnexoType])
                                              setPagination(prev => ({...prev, ignoreCache: true}))
                                          }
                                      }} disabled={!canChange} select>
                                          <MenuItem value={'OrcamentoComprovantePagamento'}
                                                    disabled={tipoSelected.includes('OrcamentoComprovantePagamento')}>
                                              Comprovante de pagamento</MenuItem>
                                          <MenuItem value={'OrcamentoDadosPagamento'}
                                                    disabled={tipoSelected.includes('OrcamentoDadosPagamento')}>Dados de
                                              pagamento</MenuItem>
                                          <MenuItem value={'NotaFiscal'}
                                                    disabled={tipoSelected.includes('NotaFiscal')}>Nota
                                              fiscal</MenuItem>
                                          <MenuItem value={'Orcamento'}
                                                    disabled={tipoSelected.includes('Orcamento')}>Orçamento</MenuItem>
                                      </TextField>
                                  </Grid>
                                  <Grid size={{xs: 12, md: 6}}>
                                      <Button fullWidth sx={{...fullHeight}} onClick={() => {
                                          addPopUp({
                                              title: 'Incluindo novo anexo',
                                              content: <CreateAnexoOrcamentoComponent
                                                  value={orcamento} events={{
                                                  onCreate: () => {
                                                      setPagination(prev => ({...prev, ignoreCache: true}))
                                                      closePopUp()
                                                  }
                                              }}/>,
                                          })
                                      }}>Anexar arquivo</Button>
                                  </Grid>
                                  <Grid size={12}>
                                      {tipoSelected.map(x => {
                                          return <Chip label={x} disabled={!canChange} onDelete={() => {
                                              setTiposSelected(prev => prev.filter(y => y != x))
                                              setPagination(prev => ({...prev, ignoreCache: true}))
                                          }}/>
                                      })}
                                  </Grid>
                              </Grid>}
                              pageProps={{pagination, setPagination}}>

        <TableContainer>
            <Table size={'small'}>
                <TableHead>
                    <TableRow>
                        <TableCell>Nome</TableCell>
                        <TableCell>Anexado por</TableCell>
                        <TableCell>Data</TableCell>
                        <TableCell>Extensao</TableCell>
                        <TableCell>Tipo</TableCell>
                        <TableCell>PreVisualizar</TableCell>
                        <TableCell>Download</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {arquivos.map((x) => {
                        return <ArquivoRow value={x} key={x.guid}/>
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    </DefaultContainer>)
}